/** Settings · Practice management (spec 15 §4–§7) — data shapes.
 *
 * One vocabulary across three pages: a reason routes to a role, a provider
 * holds roles, a room accepts roles (§6).
 */

export type Role =
  | 'general_dentist'
  | 'hygienist'
  | 'orthodontist'
  | 'periodontist'
  | 'endodontist'
  | 'oral_surgeon'
  | 'paediatric_dentist'
  | 'prosthodontist';

/** Fixed, not extendable by the practice (§5.1). */
export const ROLES: { id: Role; label: string }[] = [
  { id: 'general_dentist', label: 'General dentist' },
  { id: 'hygienist', label: 'Hygienist' },
  { id: 'orthodontist', label: 'Orthodontist' },
  { id: 'periodontist', label: 'Periodontist' },
  { id: 'endodontist', label: 'Endodontist' },
  { id: 'oral_surgeon', label: 'Oral surgeon' },
  { id: 'paediatric_dentist', label: 'Paediatric dentist' },
  { id: 'prosthodontist', label: 'Prosthodontist' },
];

export const ROLE_LABEL: Record<Role, string> = ROLES.reduce(
  (acc, r) => ({ ...acc, [r.id]: r.label }),
  {} as Record<Role, string>,
);

/* ─────────────────────────── General info (§4) ─────────────────────────── */

export interface BusinessDay {
  /** 0 = Monday … 6 = Sunday, matching the board's row order. */
  day: number;
  label: string;
  open: boolean;
  from: string;
  to: string;
}

/**
 * One list for every day the practice is shut — a public holiday, a retreat, a
 * burst pipe. `end: null` is the old "vacation mode" (§4 card 04).
 */
export interface Closure {
  id: string;
  reason: string;
  start: string;
  end: string | null;
  /** The script ELVA reads instead of the usual greeting; `{reopens}` expands. */
  script?: string;
  runningNow?: boolean;
}

export interface GeneralInfoData {
  logoInitials: string;
  practiceName: string;
  tagline: string;
  address: string;
  phone: string;
  supportEmail: string;
  website: string;
  timezone: string;
  goalWeek: string;
  goalMonth: string;
  hours: BusinessDay[];
  closures: Closure[];
  /** Empty = this location cannot place outbound calls at all (§4 card 05). */
  callDisclosure: string;
  disclosureVersion: string;
  reviewLink: string;
  bookingLink: string;
}

/* ──────────────────────────── Providers (§5) ───────────────────────────── */

export interface ProviderDay {
  label: string;
  working: boolean;
  from?: string;
  to?: string;
  /** e.g. "offered to 6:00 — the practice closes then" — computed, not typed (§5.2). */
  cappedNote?: string;
}

export interface TimeOff {
  id: string;
  from: string;
  to: string;
  /** A real field, not a free-text note (§5.2). `null` renders "Nobody covering". */
  coveredBy: string | null;
}

export type ProviderStatus = 'working' | 'away' | 'no_new_patients';

export interface Provider {
  id: string;
  name: string;
  initials: string;
  colour: string;
  /** What ELVA says out loud — never the legal name. */
  elvaSays: string;
  roles: Role[];
  bio: string;
  days: ProviderDay[];
  timeOff: TimeOff[];
  monthlyTarget: string;
  status: ProviderStatus;
  /** e.g. "back 8 Jun" on an away provider. */
  statusNote?: string;
  onlineBooking: boolean;
  acceptingNewPatients: boolean;
  mayBeDoubleBooked: boolean;
  showInCalendar: boolean;
  onRosterSince: string;
}

/* ─────────────────────────── Operatories (§6) ──────────────────────────── */

export interface Operatory {
  id: string;
  name: string;
  acceptsRoles: Role[];
  outOfService: boolean;
  /** Why it is out, e.g. "Chair being replaced — back 6 October". */
  note?: string;
  /** An appointment that reaches its start with no room assigned lands here. */
  isDefault?: boolean;
}

/* ───────────────────── Insurance & network (§7) ────────────────────────── */

export type PlanStatus = 'in_network' | 'out_of_network' | 'not_taken';

export interface Plan {
  id: string;
  name: string;
  aliases: string[];
  status: PlanStatus;
}

/** Raw exactly as the payer returned it — never normalised, never fuzzy-matched. */
export interface UnlistedPlan {
  id: string;
  rawName: string;
  patientCount: number;
  lastSeen: string;
}

export interface Carrier {
  id: string;
  name: string;
  payerId: string;
  initials: string;
  colour: string;
  aliases: string[];
  planTypes: string[];
  plans: Plan[];
  unlisted: UnlistedPlan[];
  paused: boolean;
  /** Provider ids in network with this carrier. Per carrier, not per plan (§7.4). */
  inNetworkProviderIds: string[];
}

export interface PracticeSettingsData {
  location: string;
  lastSync: string;
  general: GeneralInfoData;
  providers: Provider[];
  operatories: Operatory[];
  carriers: Carrier[];
}
